import { Download, Mail, Phone, MapPin, Globe } from "lucide-react";
import axios from "axios";
import Logo from "@/components/Logo";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bigibila Capability Statement — designed for A4 portrait print-to-PDF.
// Browser's "Save as PDF" produces the official deliverable; same page reads
// beautifully online for direct sharing of the URL.

const SERVICES = [
  {
    name: "Aboriginal Governance Architecture Toolkit™",
    status: "Ready now",
    desc: "Practical templates, registers, matrices and workflows to prepare projects for protected governance delivery.",
    deliverables: ["Diagnostic", "Authority register", "Consent forms", "30/60/90 day roadmap"],
  },
  {
    name: "BIGIBILA SYSTEMS™ Platform",
    status: "Pilot pathway",
    desc: "Protected governance software for cultural authority, ICIP, IDS, consent workflows, access controls and audit evidence.",
    deliverables: ["Controlled platform environment", "Live consent and ICIP workflows", "Cryptographic audit trail"],
  },
  {
    name: "Advisory & Architecture",
    status: "Engagement",
    desc: "Discovery, diagnostic, governance architecture and protected pilot delivery.",
    deliverables: ["Readiness scorecard", "Authority map", "Decision-rights matrix", "Pilot outcomes report"],
  },
];

const METHODS = [
  ["Bigibila Advisory Method™", "Underlying advisory methodology — diagnostic, governance design, protected delivery."],
  ["Lore-Led Architecture™", "Design approach in which software answers to Lore (Aboriginal authority), not the reverse."],
  ["Aboriginal Governance Architecture Model™ (AGAM)", "Structural governance model for authority, permission, protection, accountability and evidence."],
];

const COMPLIANCE = [
  "UNDRIP-aligned",
  "FPIC-compliant",
  "ICIP-respecting",
  "Sovereign hosting",
  "Aboriginal-owned",
];

const PHASES = [
  ["Discovery", "2–4 weeks", "Readiness scorecard · gap analysis · cultural risk register"],
  ["Architecture", "4–8 weeks", "Authority map · decision-rights matrix · ICIP/IDS registers"],
  ["Protected Pilot", "8–16 weeks", "Controlled environment · live workflows · audit evidence"],
  ["Assurance", "Ongoing", "Quarterly assurance · governance review · authority renewal"],
];

const SECTORS = [
  ["Government", "Federal, State and Local — policy, delivery, regulation."],
  ["Not-for-profit", "Sector peak bodies and community service organisations."],
  ["Justice", "Justice-sector agencies, legal aid and community justice."],
  ["Community", "Aboriginal community-controlled organisations and councils."],
];

export default function CapabilityStatement() {
  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-AU", { month: "long", year: "numeric" });
  return (
    <>
      {/* Print styles — hides site chrome (banner/nav/footer) and locks layout for A4 portrait */}
      <style>{`
        @page { size: A4 portrait; margin: 14mm 12mm; }
        @media print {
          .protective-banner, header.site-nav, footer.site-footer,
          [data-testid="protective-banner"], [data-testid="site-nav"], [data-testid="site-footer"],
          [data-print-hide="true"] { display: none !important; }
          body, html { background: #ffffff !important; }
          .cap-page { background: #ffffff !important; padding: 0 !important; }
          .cap-section { break-inside: avoid; page-break-inside: avoid; }
          .cap-break-before { break-before: page; page-break-before: always; }
          a { color: inherit; text-decoration: none; }
        }
      `}</style>

      <div className="cap-page bg-stone-100 py-10 md:py-14">
        <div className="mx-auto max-w-[860px] px-4 md:px-0">
          {/* Action bar (hidden in print) */}
          <div
            className="flex flex-wrap items-center justify-between gap-3 mb-6"
            data-print-hide="true"
            data-testid="capability-actions"
          >
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500">
              Capability Statement · A4 print ready
            </div>
            <button
              type="button"
              onClick={() => {
                trackEvent("capability_pdf_download");
                axios
                  .post(`${API}/events/capability-download`, { source: "capability_statement_page" })
                  .catch(() => {});
                window.print();
              }}
              data-testid="capability-download-pdf"
              className="inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-3 text-sm font-medium rounded-sm hover:bg-stone-800 transition-colors"
            >
              <Download className="h-4 w-4" /> Download PDF
            </button>
          </div>

          {/* The A4 document */}
          <article className="bg-[#FAFAF9] border border-stone-200 shadow-sm p-10 md:p-14">
            {/* COVER BLOCK */}
            <header className="cap-section pb-8 border-b border-stone-300">
              <div className="flex items-start justify-between gap-6">
                <Logo />
                <div className="text-right">
                  <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221]">
                    Capability statement
                  </div>
                  <div className="mt-1 text-[10px] tracking-[0.24em] uppercase text-stone-500">
                    {formattedDate}
                  </div>
                </div>
              </div>

              <h1 className="mt-10 font-display text-4xl md:text-5xl tracking-tighter font-bold text-stone-900 leading-[1.05]">
                Indigenous governance technology, designed by Country, governed by Lore.
              </h1>
              <p className="mt-5 text-stone-700 leading-relaxed max-w-2xl">
                BIGIBILA SYSTEMS™ takes the principles behind ICIP, IDS, FPIC and UNDRIP and operationalises them — through practical software controls, governance architecture and the evidence executives can show a board.
              </p>

              <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-px bg-stone-200 border border-stone-200">
                {[
                  ["Entity", "Bigibila Consultancy / BIGIBILA SYSTEMS™"],
                  ["Ownership", "Aboriginal-owned, Aboriginal-led"],
                  ["Jurisdiction", "New South Wales, Australia"],
                  ["Company number", "698 317 789"],
                  ["Founded", "2025"],
                  ["Trading status", "Operating"],
                ].map(([k, v]) => (
                  <div key={k} className="bg-[#FAFAF9] p-3">
                    <div className="text-[9px] tracking-[0.22em] uppercase font-bold text-stone-500">{k}</div>
                    <div className="mt-1 text-sm font-display font-bold text-stone-900 leading-snug">{v}</div>
                  </div>
                ))}
              </div>
            </header>

            {/* FOUNDER */}
            <section className="cap-section mt-10 pb-8 border-b border-stone-300">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
                Founder
              </div>
              <h2 className="font-display text-3xl tracking-tight font-bold text-stone-900">
                Liana Pitt
              </h2>
              <div className="mt-1 text-[10px] tracking-[0.24em] uppercase font-bold text-stone-600">
                Gamilaraay woman · Governance Architect · Founder
              </div>
              <p className="mt-4 text-stone-800 leading-relaxed text-[15px]">
                Liana Pitt is a Gamilaraay woman, governance architect and founder of Bigibila Consultancy. She is the creator of the Bigibila Advisory Method™, Lore-Led Architecture™ and the Aboriginal Governance Architecture Model™ — proprietary frameworks that support organisations to strengthen cultural authority, knowledge governance, stewardship and community-led decision-making.
              </p>
              <p className="mt-3 text-stone-700 leading-relaxed text-[14px]">
                Grounded in lineage, responsibility and connection through her family line of Arthur Dodd, Sylvia Jessica Dodd and Barbara Helen Fernando — which is where Bigibila has been passed from. Liana's work brings cultural intelligence, systems thinking and governance design together to help organisations build more accountable, respectful and lasting ways of working with Aboriginal communities.
              </p>
            </section>

            {/* WHAT WE DO */}
            <section className="cap-section mt-10 pb-8 border-b border-stone-300">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
                What we deliver
              </div>
              <h2 className="font-display text-2xl tracking-tight font-bold text-stone-900 mb-6">
                Three product lines that scale from diagnostic to enterprise licence.
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-stone-200 border border-stone-200">
                {SERVICES.map((s) => (
                  <div key={s.name} className="bg-[#FAFAF9] p-5">
                    <div className="inline-block text-[9px] tracking-[0.22em] uppercase font-bold text-[#9C4221] border border-[#9C4221]/30 bg-[#9C4221]/[0.06] px-2 py-1 mb-3">
                      {s.status}
                    </div>
                    <h3 className="font-display font-bold text-base tracking-tight text-stone-900 leading-snug">
                      {s.name}
                    </h3>
                    <p className="mt-2 text-[12px] text-stone-600 leading-relaxed">{s.desc}</p>
                    <ul className="mt-3 space-y-1">
                      {s.deliverables.map((d) => (
                        <li key={d} className="flex gap-2 text-[12px] text-stone-700 leading-snug">
                          <span className="mt-1.5 h-1 w-1 bg-[#9C4221] rounded-full shrink-0" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* METHODOLOGY */}
            <section className="cap-section mt-10 pb-8 border-b border-stone-300">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
                Methodology — proprietary IP
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {METHODS.map(([title, desc]) => (
                  <div key={title} className="border border-stone-300 bg-[#FAFAF9] p-4">
                    <h3 className="font-display font-bold text-sm tracking-tight text-stone-900 leading-snug">
                      {title}
                    </h3>
                    <p className="mt-2 text-[12px] text-stone-600 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* COMPLIANCE & ALIGNMENT */}
            <section className="cap-section mt-10 pb-8 border-b border-stone-300">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
                Compliance & alignment
              </div>
              <div className="flex flex-wrap gap-2">
                {COMPLIANCE.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center gap-2 border border-stone-300 bg-[#FAFAF9] px-3 py-1.5 text-[11px] font-semibold tracking-wide text-stone-800"
                  >
                    <span className="h-1.5 w-1.5 bg-[#9C4221] rounded-full" />
                    {c}
                  </span>
                ))}
              </div>
              <p className="mt-4 text-[12px] text-stone-600 leading-relaxed max-w-3xl">
                Architecture aligned with the United Nations Declaration on the Rights of Indigenous Peoples (UNDRIP). Consent workflows designed against Free, Prior and Informed Consent (FPIC). Asset governance respects Indigenous Cultural and Intellectual Property (ICIP). Data governance follows Indigenous Data Sovereignty (IDS) principles. Australian-hosted sovereign infrastructure.
              </p>
            </section>

            {/* ENGAGEMENT PATHWAY */}
            <section className="cap-section mt-10 pb-8 border-b border-stone-300">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
                Engagement pathway
              </div>
              <h2 className="font-display text-2xl tracking-tight font-bold text-stone-900 mb-5">
                Four phases, executive decision gates between each.
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-stone-200 border border-stone-200">
                {PHASES.map(([name, duration, deliverables], i) => (
                  <div key={name} className="bg-[#FAFAF9] p-4">
                    <div className="font-display text-2xl tracking-tight text-stone-300 font-bold">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className="mt-1 font-display font-bold text-sm tracking-tight text-stone-900">
                      {name}
                    </h3>
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#9C4221] mt-1">
                      {duration}
                    </div>
                    <p className="mt-3 text-[11px] text-stone-700 leading-snug">{deliverables}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* SECTORS */}
            <section className="cap-section mt-10 pb-8 border-b border-stone-300">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
                Sectors served
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {SECTORS.map(([name, desc]) => (
                  <div key={name} className="border border-stone-300 bg-[#FAFAF9] p-4">
                    <h3 className="font-display font-bold text-sm tracking-tight text-stone-900">
                      {name}
                    </h3>
                    <p className="mt-1 text-[12px] text-stone-600 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* CONTACT */}
            <section className="cap-section mt-10">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] mb-3">
                Engage with us
              </div>
              <h2 className="font-display text-2xl tracking-tight font-bold text-stone-900 leading-snug">
                Request a confidential discovery call.
              </h2>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-200 border border-stone-200">
                <div className="bg-[#FAFAF9] p-4 flex items-start gap-3">
                  <Mail className="h-4 w-4 mt-0.5 text-[#9C4221] shrink-0" strokeWidth={2} />
                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500">Email</div>
                    <div className="font-display font-bold text-sm text-stone-900">
                      info@bigibilasystems.com
                    </div>
                    <div className="text-[10px] text-stone-500 italic">General enquiries, capability &amp; procurement.</div>
                  </div>
                </div>
                <div className="bg-[#FAFAF9] p-4 flex items-start gap-3">
                  <Globe className="h-4 w-4 mt-0.5 text-[#9C4221] shrink-0" strokeWidth={2} />
                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500">Website</div>
                    <div className="font-display font-bold text-sm text-stone-900">www.bigibilasystems.com.au</div>
                    <div className="text-[10px] text-stone-500">Self-assessment: www.bigibilasystems.com.au/readiness</div>
                  </div>
                </div>
                <div className="bg-[#FAFAF9] p-4 flex items-start gap-3">
                  <Phone className="h-4 w-4 mt-0.5 text-[#9C4221] shrink-0" strokeWidth={2} />
                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500">Mobile</div>
                    <div className="font-display font-bold text-sm text-stone-900">0466 943 229</div>
                  </div>
                </div>
                <div className="bg-[#FAFAF9] p-4 flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-0.5 text-[#9C4221] shrink-0" strokeWidth={2} />
                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500">Country</div>
                    <div className="font-display font-bold text-sm text-stone-900">Gamilaraay / Yuwaalaraay Country, NSW</div>
                  </div>
                </div>
              </div>

              <p className="mt-8 text-[11px] text-stone-500 leading-relaxed border-t border-stone-200 pt-5">
                BIGIBILA SYSTEMS™, Bigibila Advisory Method™, Lore-Led Architecture™, Aboriginal Governance Architecture Model™ and AGAM Toolkit™ are trademarks (registered or asserted) of Bigibila Consultancy. This document is a capability summary, not legal advice. The platform is software for governance; it does not replace cultural authority or community decision-making.
              </p>
              <p className="mt-3 text-[10px] tracking-[0.24em] uppercase font-bold text-stone-500">
                Strong Foundations · Living Governance · Sovereign Futures
              </p>
            </section>
          </article>
        </div>
      </div>
    </>
  );
}
